import { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Paper, CssBaseline, TableContainer, Table,
  TableHead, TableRow, TableCell, TableBody, IconButton, TablePagination,
  useMediaQuery, useTheme, Avatar, Backdrop, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Header from '../Includes/Header';
import { useNavigate } from 'react-router-dom';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';

const formatDateDMY = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${d.getFullYear()}`;
};

const PtpaymentManagement = () => {
  const { colors: appColors } = useAppTheme();
  const [trainerList, setTrainerList] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [ptPackages, setPtPackages] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchTrainerList = async () => {
      const userData = localStorage.getItem('userdata');
      const userDataObj = JSON.parse(userData);
      const userToken = userDataObj.token;

      setLoading(true);
      try {
        const response = await fetch(`${window.constants.api_url}/trainer/pt/list`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userToken}`
          }
        });

        const result = await response.json();
        if (result.code === 200) {
          setTrainerList(Array.isArray(result.data) ? result.data : []);
        } else {
          setTrainerList([]);
        }
      } catch (error) {
        console.error('Failed to fetch trainers', error);
        setTrainerList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTrainerList();
  }, []);

  const handleTrainerClick = async (trainer) => {
    setSelectedTrainer(trainer);
    setPage(0);
    fetchPtPackages(trainer.id, 0, rowsPerPage);
  };

  const fetchPtPackages = async (emp_id, pageNumber, limit) => {
    const userData = JSON.parse(localStorage.getItem('userdata'));
    const userToken = userData?.token;

    setLoading(true);
    try {
      const response = await fetch(`${window.constants.api_url}/trainer-wise-pt-packages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          emp_id,
          page: pageNumber + 1,
          limit,
        }),
      });

      const result = await response.json();
      if (result.code === 200) {
        setPtPackages(result.data || []);
        setTotalRecords(result.total_records || 0);
      }
    } catch (err) {
      console.error('Error fetching PT packages', err);
      setPtPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const closeTable = () => {
    setSelectedTrainer(null);
    setPtPackages([]);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    fetchPtPackages(selectedTrainer.id, newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    fetchPtPackages(selectedTrainer.id, 0, newLimit);
  };
  const handleCardPress = async (id) => {
    try {
      navigate('/memberdetails', {
        state: {
          id: id,
          random_no: Math.random(),
        },
      });
    } catch (error) {
      console.error("Error:", error);
    }
  };
  return (
    <Box sx={{ display: 'flex', height: '100vh', backgroundColor: '#f1f5f9' }}>
      <CssBaseline />
      
      <Backdrop
  open={loading}
  sx={{
    color: '#fff',
    zIndex: (theme) => theme.zIndex.drawer + 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'fixed',
    top: 0,
   
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Optional: adds translucent black bg
  }}
>
  <CircularProgress color="inherit" size={60} thickness={5} />
</Backdrop>

      <Box>
        <Header />
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          marginTop: isMobile ? '10px' : '50px',
          padding: isMobile ? '10px' : '20px',
        }}
      >
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 0.5, 
            fontWeight: 600,
            color: appColors.evergreen
          }}
        >
          PT Payment Management
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
          Members who took PT and their payments
        </Typography>

        {!selectedTrainer && (
          <>
          {!loading && trainerList.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 5,
                textAlign: 'center',
                borderRadius: '16px',
                backgroundColor: '#fff',
                boxShadow: '0 1px 3px rgba(27,67,50,0.08)',
                border: '1px solid rgba(27,67,50,0.06)',
              }}
            >
              <Typography variant="h6" sx={{ color: '#475569', fontWeight: 600 }}>
                No PT payment records
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1 }}>
                No PT payments have been recorded yet.
              </Typography>
            </Paper>
          ) : (
          <>
          {trainerList.length > 0 && (
            <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 2 }}>
              View PT payments by trainer
            </Typography>
          )}
          <Grid container spacing={isMobile ? 2 : 3}>
            {trainerList.map((trainer) => (
           <Grid   size={{ xs: 12,sm:6,md:4,lg:3}} key={trainer.id}>
           <Paper
             elevation={3}
             sx={{
               p: isMobile ? 2 : 3,
               borderRadius: 4,
               textAlign: 'center',
               cursor: 'pointer',
               background: `linear-gradient(135deg, ${appColors.evergreen} 0%, ${appColors.leaf} 100%)`,
               color: '#fff',
               transition: 'all 0.3s ease-in-out',
               boxShadow: '0 6px 20px rgba(27,67,50,0.35)',
               '&:hover': {
                 transform: 'translateY(-6px)',
                 boxShadow: '0 12px 30px rgba(27,67,50,0.45)',
               },
             }}
             onClick={() => handleTrainerClick(trainer)}
           >
            <Avatar
  src={trainer.image}
  alt={trainer.name}
  sx={{
    width: 72,
    height: 72,
    margin: '0 auto',
    mb: 2,
    border: '2px solid #fff',
    boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
    bgcolor: appColors.leaf,
  }}
/>
             <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff' }}>
               {trainer.name}
             </Typography>
             <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
               {trainer.designation}
             </Typography>
             <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block', mt: 0.5 }}>
               View PT payments
             </Typography>
           </Paper>
         </Grid>
         
            ))}
          </Grid>
          </>
          )}
          </>
        )}

        {selectedTrainer && (
          <Box mt={4}>
            <Paper 
              sx={{ 
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  p: 2,
                  bgcolor: appColors.evergreen,
                  color: 'white'
                }}
              >
              <Typography variant="h6">
                PT payments – {selectedTrainer.name}
              </Typography>
                <IconButton onClick={closeTable} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
              
              <TableContainer>
              <Table>
                <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      {['Date', 'Member', 'Membership ID', 'Package', 'Timing', 'Amount'].map((header) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontWeight: 600,
                            color: '#1e293b',
                            py: 2,
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && ptPackages.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#64748b', fontWeight: 500 }}>
                        No PT payments for this trainer yet
                      </TableCell>
                    </TableRow>
                  ) : (
                  ptPackages.map((row, index) => (
                      <TableRow 
                        key={index}
                        sx={{
                          '&:nth-of-type(odd)': {
                            backgroundColor: '#f8fafc',
                          },
                          '&:hover': {
                            backgroundColor: '#f1f5f9',
                          }
                        }}
                      >
                        <TableCell sx={{ color: '#475569' }}>{formatDateDMY(row.date_of_payment)}</TableCell>
                        <TableCell sx={{ color: '#1e293b', fontWeight: 500 }}>{row.member_name}</TableCell>
                        <TableCell onClick={() => handleCardPress(row.member_id)}>
                          <Typography 
                            sx={{
                              color: appColors.leaf,
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              '&:hover': {
                                color: appColors.evergreen
                              }
                            }}
                          >
                            {row.membership_number}
                          </Typography>
                   </TableCell>
                        <TableCell sx={{ color: '#475569' }}>{row.package_name}</TableCell>
                        <TableCell sx={{ color: '#475569' }}>{row.slot}</TableCell>
                        <TableCell sx={{ color: '#475569' }}>₹{row.paying_amount}</TableCell>
                    </TableRow>
                  ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    '.MuiTablePagination-select': {
                      color: '#1e293b'
                    },
                    '.MuiTablePagination-displayedRows': {
                      color: '#475569'
                    }
                  }}
              />
            </TableContainer>
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PtpaymentManagement;
